/**
 * Sending a finished Leaderboard game's result to the relay, and keeping the ones that couldn't be sent yet.
 *
 * The queue IS the saved games: a game is waiting to be sent when it is a leaderboard game that has ended and
 * has neither `submittedAt` (accepted) nor `rejected` (refused for a reason a retry can't fix). Nothing else is
 * stored, so a restart, a restored backup or a second tab can never disagree about what is still owed.
 *
 * The relay treats a repeat submission as a harmless duplicate, so retrying is always safe.
 */

import { summarise } from '../game/summary';
import type { GameStore } from '../game/store';
import type { Game } from '../game/types';
import { submitLeaderboardResult, type LeaderboardResultPayload, type SubmitOutcome } from './leaderboardClient';

/** Whether this game's result is still owed to the leaderboard. */
export function isAwaitingSubmission(game: Game): boolean {
  const lb = game.leaderboard;
  return !!lb && game.endedAt !== undefined && lb.submittedAt === undefined && lb.rejected === undefined;
}

/**
 * The result to send for an ended leaderboard game, or an explanation of why it must not be sent. Scoring stays
 * on this device (rule 1): these are the totals the summary screen already shows. The settlement invariant of the
 * period's style is checked here too (rule 3), so a bug that invented or lost points fails loudly on the host's
 * device instead of being sent.
 */
export function resultFor(game: Game): { ok: true; payload: LeaderboardResultPayload } | { ok: false; error: string } {
  const lb = game.leaderboard;
  if (!lb) return { ok: false, error: 'This is not a leaderboard game.' };
  const summary = summarise(game);
  const results = summary.players.map((p) => ({ nickname: p.name, points: p.totalScore, wins: p.roundsWon }));
  const total = results.reduce((sum, r) => sum + r.points, 0);
  if (lb.settlement === 'zero_sum' && total !== 0) {
    return { ok: false, error: "The scores in this game don't add up to zero, so the result can't be sent." };
  }
  if (lb.settlement === 'winner_only' && results.some((r) => r.points < 0 || (r.wins === 0 && r.points !== 0))) {
    return { ok: false, error: "The scores in this game don't fit winner-only scoring, so the result can't be sent." };
  }
  return { ok: true, payload: { gameId: lb.gameId, rounds: summary.roundsPlayed, results } };
}

/** Requests already in flight, so a double tap (or a background retry racing a tap) makes one request, not two. */
const inFlight = new Map<string, Promise<SubmitOutcome>>();

/**
 * Sends one game's result and records what came of it on the game and its saved copy: `submittedAt` when
 * accepted, `rejected` (with the reason) when refused. A network problem or an expired session records nothing —
 * the game just stays owed. Only the REQUEST is shared between simultaneous callers; each applies the outcome to
 * its own copy of the game, so whichever copy a screen is holding ends up current. A game that is not owed
 * answers `sent` without a request.
 */
export async function sendGameResult(store: GameStore, game: Game): Promise<SubmitOutcome> {
  const lb = game.leaderboard;
  if (!lb || lb.submittedAt !== undefined) return { kind: 'sent', duplicate: true };

  let request = inFlight.get(game.id);
  if (!request) {
    const built = resultFor(game);
    request = (built.ok
      ? submitLeaderboardResult(built.payload)
      : Promise.resolve<SubmitOutcome>({ kind: 'rejected', code: 'local', message: built.error })
    ).finally(() => inFlight.delete(game.id));
    inFlight.set(game.id, request);
  }
  const outcome = await request;

  if (outcome.kind === 'sent') {
    lb.submittedAt = Date.now();
    delete lb.rejected;
    await store.save(game);
  } else if (outcome.kind === 'rejected') {
    lb.rejected = { code: outcome.code, message: outcome.message };
    await store.save(game);
  }
  return outcome;
}

export interface FlushReport {
  sent: number;
  rejected: number;
  /** Still owed afterwards: the network or relay was unavailable, or the device needs activating again. */
  stillOwed: number;
  /** True when at least one attempt found the session expired. */
  needsActivation: boolean;
}

/** Tries every owed game, one after another. Never throws: a failure just leaves that game owed. */
export async function flushOwedResults(store: GameStore): Promise<FlushReport> {
  const report: FlushReport = { sent: 0, rejected: 0, stillOwed: 0, needsActivation: false };
  let games: Game[];
  try {
    games = (await store.listAll()).filter(isAwaitingSubmission);
  } catch {
    return report;
  }
  for (const game of games) {
    const outcome = await sendGameResult(store, game).catch((): SubmitOutcome => ({ kind: 'retry', reason: '' }));
    if (outcome.kind === 'sent') report.sent++;
    else if (outcome.kind === 'rejected') report.rejected++;
    else {
      report.stillOwed++;
      if (outcome.kind === 'reactivate') {
        report.needsActivation = true;
        break; // every later attempt would fail the same way
      }
    }
  }
  return report;
}

/**
 * The owed games that include any of these nicknames. The relay abandons a player's earlier open game when they
 * start a new one, so a player must not start a new leaderboard game while a finished one of theirs is unsent.
 */
export function owedGamesWith(games: Game[], nicknames: string[]): Game[] {
  const wanted = new Set(nicknames.map((n) => n.trim().toLowerCase()));
  return games.filter((g) => isAwaitingSubmission(g) && g.players.some((p) => wanted.has(p.name.trim().toLowerCase())));
}

/** What the results screen shows for a leaderboard game right now. */
export type SubmissionStatus =
  | { kind: 'sent' }
  | { kind: 'sending' }
  | { kind: 'owed'; reason?: string }
  | { kind: 'rejected'; message: string };

export function statusOf(game: Game, sending: boolean, lastReason?: string): SubmissionStatus | undefined {
  const lb = game.leaderboard;
  if (!lb) return undefined;
  if (lb.submittedAt !== undefined) return { kind: 'sent' };
  if (lb.rejected) return { kind: 'rejected', message: lb.rejected.message };
  if (sending) return { kind: 'sending' };
  return { kind: 'owed', ...(lastReason ? { reason: lastReason } : {}) };
}
